import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../../config/config';

const PROVIDERS = [
  { id: 'github_models' },
  { id: 'copilot' },
  { id: 'openai' },
  { id: 'anthropic' },
  { id: 'ollama' },
];

function AiSettings() {
  const { t } = useTranslation();
  const [provider, setProvider] = useState('github_models');
  const [model, setModel] = useState('gpt-4o');
  const [apiKey, setApiKey] = useState('');
  const [ollamaUrl, setOllamaUrl] = useState('http://localhost:11434');
  const [models, setModels] = useState([]);
  const [status, setStatus] = useState(null); // { type: 'success'|'danger', msg }
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [modelsError, setModelsError] = useState('');

  // Fetch current settings on mount
  useEffect(() => {
    setLoadError('');
    axios.get(`${config.BASE_URL}/ai/settings`).then(r => {
      setProvider(r.data.provider || 'copilot');
      setModel(r.data.model || 'gpt-4o');
      setApiKey(r.data.apiKey || '');
      setOllamaUrl(r.data.ollamaUrl || 'http://localhost:11434');
    }).catch((e) => {
      setLoadError(e.response?.data?.error || e.message || 'Failed to load AI settings.');
    });
  }, []);

  // Fetch models when provider changes
  const fetchModels = useCallback((p) => {
    setModelsError('');
    axios.get(`${config.BASE_URL}/ai/settings/models`, { params: { provider: p } })
      .then(r => {
        setModels(r.data || []);
        if (r.data && r.data.length > 0) setModel(r.data[0]);
      }).catch((e) => {
        setModels([]);
        setModelsError(e.response?.data?.error || e.message || 'Failed to load available models.');
      });
  }, []);

  useEffect(() => { fetchModels(provider); }, [provider, fetchModels]);

  const handleSave = () => {
    setSaving(true);
    setStatus(null);
    axios.put(`${config.BASE_URL}/ai/settings`, { provider, model, apiKey, ollamaUrl })
      .then(() => setStatus({ type: 'success', msg: `✅ ${t('aiSettings.saved')}` }))
      .catch(e => setStatus({ type: 'danger', msg: `❌ ${e.response?.data?.error || e.message}` }))
      .finally(() => setSaving(false));
  };

  const handleTest = () => {
    setTesting(true);
    setStatus(null);
    axios.post(`${config.BASE_URL}/ai/settings/test`, { provider, model, apiKey, ollamaUrl })
      .then(() => setStatus({ type: 'success', msg: `✅ ${t('aiSettings.testOk')}` }))
      .catch(e => setStatus({ type: 'danger', msg: `❌ ${e.response?.data?.error || e.message}` }))
      .finally(() => setTesting(false));
  };

  return (
    <div className="container py-4" style={{ maxWidth: 640 }}>
      <h4 className="mb-4">⚙️ {t('aiSettings.title')}</h4>

      <div className="card shadow-sm">
        <div className="card-body">
          {loadError && <div className="alert alert-danger py-2 small">{loadError}</div>}
          {modelsError && <div className="alert alert-warning py-2 small">{modelsError}</div>}

          {/* Provider */}
          <div className="mb-4">
            <label className="form-label fw-semibold">{t('aiSettings.provider')}</label>
            <div className="d-flex flex-wrap gap-3">
              {PROVIDERS.map(p => (
                <div key={p.id} className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="provider"
                    id={`provider-${p.id}`}
                    value={p.id}
                    checked={provider === p.id}
                    onChange={() => setProvider(p.id)}
                  />
                  <label className="form-check-label" htmlFor={`provider-${p.id}`}>{t(`aiSettings.providers.${p.id}`)}</label>
                </div>
              ))}
            </div>
            {provider === 'github_models' && (
              <div className="alert alert-success py-2 mt-2 small mb-0">
                ✅ {t('aiSettings.githubModelsHint')}
              </div>
            )}
            {provider === 'copilot' && (
              <div className="alert alert-success py-2 mt-2 small mb-0">
                ✅ {t('aiSettings.copilotHint')}
              </div>
            )}
          </div>

          {/* Model */}
          <div className="mb-3">
            <label className="form-label fw-semibold">{t('aiSettings.model')}</label>
            <select
              className="form-select"
              value={model}
              onChange={e => setModel(e.target.value)}
            >
              {models.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {/* API Key (hidden for Ollama and Copilot CLI) */}
          {provider !== 'ollama' && provider !== 'copilot' && (
            <div className="mb-3">
              <label className="form-label fw-semibold">
                {provider === 'github_models' ? t('aiSettings.githubToken') : t('aiSettings.apiKey')}
              </label>
              <input
                type="password"
                className="form-control"
                placeholder={provider === 'github_models' ? 'gho_... of ghp_...' : provider === 'copilot' ? 'ghp_...' : 'sk-...'}
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                autoComplete="off"
              />
            </div>
          )}

          {/* Ollama URL */}
          {provider === 'ollama' && (
            <div className="mb-3">
              <label className="form-label fw-semibold">{t('aiSettings.ollamaUrl')}</label>
              <input
                type="text"
                className="form-control"
                value={ollamaUrl}
                onChange={e => setOllamaUrl(e.target.value)}
              />
            </div>
          )}

          {/* Status */}
          {status && (
            <div className={`alert alert-${status.type} py-2 small`}>{status.msg}</div>
          )}

          {/* Buttons */}
          <div className="d-flex gap-2">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
              {saving ? t('aiSettings.saving') : t('aiSettings.save')}
            </button>
            <button className="btn btn-outline-secondary" onClick={handleTest} disabled={testing}>
              {testing ? <span className="spinner-border spinner-border-sm me-1" /> : null}
              {testing ? t('aiSettings.testing') : t('aiSettings.test')}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

export default AiSettings;
